import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import { TourV2ContentSections } from 'constants/tours'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import OfferPageStickyFooter from 'components/OfferPage/OfferPageStickyFooter'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import TourV2DepartureBookingModal from '../Departures/TourV2DepartureBookingModal'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import PriceRowLuxPlusPriceWithCaption from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import { getTourPricePerDay, getTourV2VariationDuration } from 'lib/tours/tourUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'

interface Props {
  purchasableOption?: App.Tours.TourV2OfferPurchasableOption
  onScrollTo: (section: TourV2ContentSections) => void
  visible: boolean
  offer: App.Tours.TourV2Offer
  variation: App.Tours.TourV2OfferVariation
}

function TourV2StickyFooter(props: Props) {
  const { purchasableOption, onScrollTo, visible, offer, variation } = props

  const intl = useIntl()
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)
  const tourPriceMode = useAppSelector(getTourPriceMode)

  const duration = useMemo(() => {
    return getTourV2VariationDuration(variation)
  }, [variation])

  const pricePerDayDetails = useMemo(() => {
    if (!purchasableOptionDetails || tourPriceMode === 'total') return
    return getTourPricePerDay(purchasableOptionDetails.pricePerPerson, purchasableOptionDetails.memberPricePerPerson, duration)
  }, [purchasableOptionDetails, duration, tourPriceMode])

  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const onCheckAvailability = useCallback(() => {
    if (isMobile) {
      showModal(<TourV2DepartureBookingModal offer={offer} />)
    } else {
      onScrollTo(TourV2ContentSections.DEPARTURES)
    }
  }, [isMobile, offer, onScrollTo, showModal])

  return (
    <OfferPageStickyFooter
      visible={visible}
      callToActionCopy={intl.formatMessage({
        id: 'ju5eu6jai4',
        defaultMessage: 'Check Availability',
        description: 'CTA button text',
      })}
      onCTAClick={onCheckAvailability}
    >
      {!!purchasableOptionDetails && <div>
        {!pricePerDayDetails && <>
          {!purchasableOptionDetails.memberPricePerPerson && <PriceRowPriceWithCaption
            size="M"
            caption={intl.formatMessage({
              id: 'XPUGPCuQCW',
              defaultMessage: 'From',
              description: 'Price caption prefix',
            })}
            price={purchasableOptionDetails.pricePerPerson}
            saleUnit={intl.formatMessage({
              id: '+jTWKJKNCF',
              defaultMessage: 'person',
              description: 'Sale unit for per person pricing',
            })}
          />}
          {!!purchasableOptionDetails.memberPricePerPerson && <PriceRowLuxPlusPriceWithCaption
            size="M"
            caption={intl.formatMessage({
              id: 'XPUGPCuQCW',
              defaultMessage: 'From',
              description: 'Price caption prefix',
            })}
            memberPrice={purchasableOptionDetails.memberPricePerPerson}
            saleUnit={intl.formatMessage({
              id: '+jTWKJKNCF',
              defaultMessage: 'person',
              description: 'Sale unit for per person pricing',
            })}
          />}
        </>}
        {
          pricePerDayDetails &&
          <>
            {!pricePerDayDetails?.memberPricePerPersonPerDay && <PriceRowPriceWithCaption
              size="M"
              caption={intl.formatMessage({
                id: 'XPUGPCuQCW',
                defaultMessage: 'From',
                description: 'Price caption prefix',
              })}
              price={pricePerDayDetails.pricePerPersonPerDay}
              saleUnit={intl.formatMessage({
                id: 'LW957FgwsU',
                defaultMessage: 'day per person',
                description: 'Sale unit for per day per person pricing',
              })}
            />}
            {!!pricePerDayDetails.memberPricePerPersonPerDay && <PriceRowLuxPlusPriceWithCaption
              size="M"
              caption={intl.formatMessage({
                id: 'XPUGPCuQCW',
                defaultMessage: 'From',
                description: 'Price caption prefix',
              })}
              memberPrice={pricePerDayDetails.memberPricePerPersonPerDay}
              saleUnit={intl.formatMessage({
                id: 'LW957FgwsU',
                defaultMessage: 'day per person',
                description: 'Sale unit for per day per person pricing',
              })}
            />}
          </>
        }

        <PriceRowCaption>
          {purchasableOptionDetails.roomTypeName}
        </PriceRowCaption>
      </div>}
    </OfferPageStickyFooter>
  )
}

type MappedProps = Pick<Props, 'offer'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.tourV2Offer!,
}), TourV2StickyFooter)
